import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, FileText, MessageSquare, CheckCircle, Clock, Send, AlertTriangle, LogOut, Edit3, ChevronRight } from 'lucide-react';
import ClientChat from './ClientChat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ClientDashboard() {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [activeTab, setActiveTab] = useState('courrier');
  const [loading, setLoading] = useState(true);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const clientInfo = JSON.parse(localStorage.getItem('client_info') || '{}');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const getToken = () => localStorage.getItem('client_token');

  const fetchDossiers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/client/dossiers`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setDossiers(res.data.dossiers || []);
    } catch {
      navigate('/espace-client/connexion');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchDossierDetail = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/client/dossiers/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSelectedDossier(res.data);
      setActiveTab('courrier');
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!getToken()) {
      navigate('/espace-client/connexion');
      return;
    }
    fetchDossiers();
  }, [fetchDossiers, navigate]);

  const submitAnswer = async (dossierId, questionId) => {
    const answer = answers[questionId];
    if (!answer?.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/client/dossiers/${dossierId}/answer`, {
        question_id: questionId,
        answer: answer
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      showToast('Réponse envoyée');
      setAnswers(prev => ({ ...prev, [questionId]: '' }));
      fetchDossierDetail(dossierId);
    } catch (err) {
      showToast('Erreur lors de l\'envoi', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const submitFeedback = async (dossierId, type) => {
    if (type === 'revision_requested' && !feedbackMessage.trim()) {
      showToast('Veuillez décrire les modifications souhaitées.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/client/dossiers/${dossierId}/feedback`, {
        feedback_type: type,
        message: feedbackMessage
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      showToast(res.data.message);
      setFeedbackMessage('');
      fetchDossierDetail(dossierId);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Erreur', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_info');
    navigate('/espace-client/connexion');
  };

  const statusLabel = (s) => {
    const map = {
      draft: 'Brouillon', analyzing: 'En analyse', validating: 'En traitement',
      sent: 'Prêt à consulter', completed: 'Finalisé', error: 'Erreur'
    };
    return map[s] || s;
  };

  const statusColor = (s) => {
    const map = {
      draft: 'bg-secondary text-foreground', analyzing: 'bg-blue-100 text-blue-800',
      validating: 'bg-amber-100 text-amber-800', sent: 'bg-green-100 text-green-800',
      completed: 'bg-green-100 text-green-800', error: 'bg-red-100 text-red-800'
    };
    return map[s] || 'bg-secondary text-foreground';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`} data-testid="toast-message">
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-accent" />
            <h1 className="text-base sm:text-lg font-semibold text-primary">Espace Client</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden sm:inline">{clientInfo.prenom} {clientInfo.nom}</span>
            <button onClick={logout} className="text-muted-foreground hover:text-foreground" data-testid="client-logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {!selectedDossier ? (
          /* ===== DOSSIER LIST ===== */
          <div>
            <h2 className="text-xl font-semibold mb-4" data-testid="dossier-list-title">Mes dossiers</h2>
            {dossiers.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Aucun dossier pour le moment.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {dossiers.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => fetchDossierDetail(d.id)}
                    className="w-full bg-card border border-border rounded-lg p-4 hover:border-accent/50 transition-all text-left flex items-center justify-between gap-3"
                    data-testid={`dossier-card-${d.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">Dossier {d.id.substring(0, 8).toUpperCase()}</p>
                      <p className="text-xs text-muted-foreground capitalize">{d.problem_type || 'Administratif'}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs px-2 py-1 rounded font-medium ${statusColor(d.status)}`}>
                        {statusLabel(d.status)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* ===== DOSSIER DETAIL ===== */
          <div>
            {/* Back button */}
            <button
              onClick={() => setSelectedDossier(null)}
              className="text-sm text-muted-foreground hover:text-foreground mb-4 flex items-center gap-1"
              data-testid="back-to-dossiers"
            >
              Mes dossiers
            </button>

            {/* Dossier header */}
            <div className="flex flex-col sm:flex-row items-start justify-between gap-3 mb-5">
              <div>
                <h2 className="text-lg font-semibold">Dossier {selectedDossier.id.substring(0, 8).toUpperCase()}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-1 rounded font-medium ${statusColor(selectedDossier.status)}`}>
                    {statusLabel(selectedDossier.status)}
                  </span>
                  {selectedDossier.estimated_delivery?.label && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Livraison : {selectedDossier.estimated_delivery.label}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  Modifications : {selectedDossier.revision_count}/{selectedDossier.max_revisions}
                </span>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-1 mb-4 border-b border-border overflow-x-auto" data-testid="client-tabs">
              {[
                { id: 'courrier', label: 'Mon courrier', icon: FileText },
                { id: 'messagerie', label: 'Messagerie', icon: MessageSquare },
                { id: 'feedback', label: 'Mon avis', icon: Edit3 }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-all ${
                    activeTab === tab.id
                      ? 'border-b-2 border-accent text-accent -mb-px'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`client-tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* TAB: Courrier */}
            {activeTab === 'courrier' && (
              <div data-testid="client-tab-content-courrier">
                {selectedDossier.final_letter ? (
                  <div className="bg-white border border-border rounded-lg p-5 sm:p-8 shadow-sm">
                    <pre className="whitespace-pre-wrap text-sm font-serif leading-relaxed text-foreground">
                      {selectedDossier.final_letter}
                    </pre>
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-lg p-8 text-center">
                    <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <h3 className="font-semibold text-foreground mb-2">En cours de rédaction</h3>
                    <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                      Votre courrier est en cours de préparation par notre équipe. 
                      Vous recevrez une notification dès qu'il sera prêt.
                    </p>
                    {selectedDossier.estimated_delivery?.label && (
                      <p className="text-sm text-accent font-medium mt-3">
                        Délai estimé : {selectedDossier.estimated_delivery.label}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* TAB: Messagerie / Chat */}
            {activeTab === 'messagerie' && (
              <div data-testid="client-tab-content-messagerie">
                <ClientChat dossierId={selectedDossier.id} />
              </div>
            )}

            {/* TAB: Feedback / Avis */}
            {activeTab === 'feedback' && (
              <div data-testid="client-tab-content-feedback">
                {selectedDossier.client_feedback === 'approved' ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-3" />
                    <h3 className="font-semibold text-green-800 mb-2">Courrier approuvé</h3>
                    <p className="text-sm text-green-700">Votre courrier a été finalisé. Merci de votre confiance.</p>
                  </div>
                ) : !selectedDossier.final_letter ? (
                  <div className="bg-card border border-border rounded-lg p-6 text-center">
                    <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      Votre courrier est en cours de préparation. Vous pourrez donner votre avis une fois la rédaction terminée.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Approve */}
                    <div className="bg-card border border-border rounded-lg p-5">
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Le courrier me convient
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        Si votre courrier vous satisfait, confirmez pour finaliser votre dossier.
                      </p>
                      <button
                        onClick={() => submitFeedback(selectedDossier.id, 'approved')}
                        disabled={submitting}
                        className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg font-medium text-sm disabled:opacity-50"
                        data-testid="approve-letter-btn"
                      >
                        Approuver le courrier
                      </button>
                    </div>

                    {/* Request revision */}
                    {selectedDossier.revision_count < selectedDossier.max_revisions && (
                      <div className="bg-card border border-border rounded-lg p-5">
                        <h3 className="font-semibold mb-2 flex items-center gap-2">
                          <Edit3 className="w-4 h-4 text-amber-600" />
                          Demander une modification
                        </h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          {selectedDossier.max_revisions - selectedDossier.revision_count} modification(s) restante(s) sur {selectedDossier.max_revisions}.
                        </p>
                        <textarea
                          value={feedbackMessage}
                          onChange={(e) => setFeedbackMessage(e.target.value)}
                          placeholder="Décrivez les modifications souhaitées..."
                          rows={4}
                          className="w-full border border-border rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-accent/50 resize-none"
                          data-testid="revision-textarea"
                        />
                        <button
                          onClick={() => submitFeedback(selectedDossier.id, 'revision_requested')}
                          disabled={submitting || !feedbackMessage.trim()}
                          className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 rounded-lg font-medium text-sm disabled:opacity-50"
                          data-testid="submit-revision-btn"
                        >
                          Envoyer la demande
                        </button>
                      </div>
                    )}

                    {selectedDossier.revision_count >= selectedDossier.max_revisions && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Nombre maximum de modifications atteint</p>
                          <p className="text-xs text-amber-700 mt-1">
                            Vous avez utilisé vos {selectedDossier.max_revisions} modifications. 
                            Si vous avez besoin de changements supplémentaires, contactez notre équipe.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientDashboard;
